// frontend/src/pages/WorkshopLogMaintenance.js
// Log a maintenance job from a confirmed quotation — chrome/ink, French.
// The service-reason modal is the last step before the API call, so the whole
// payload (including any lines the workshop did not fit) goes out in one shot.

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { fetchAssignedVehicles } from '../api/vehicles';
import { fetchConfirmedQuotations } from '../api/workshopQuotation';
import { logFromQuotation, fetchVehicleHistory } from '../api/maintenance';
import SearchableSelect from '../components/SearchableSelect';
import {
  Page, Panel, Button, Field, Modal, Checkbox, ErrorNote, Note,
  inputClass, selectClass, textareaClass,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

// Stable fallback for useQuery's `data = ...` default. A literal `[]` there is
// a fresh array on every render, which makes any effect or memo keyed on the
// query data re-run forever.
const EMPTY = [];

const SERVICE_OPTIONS = [
  { value: 'vidange', label: 'Vidange' },
  { value: 'frein', label: 'Plaquettes de frein' },
  { value: 'filtre_air', label: 'Filtre à air' },
  { value: 'filtre_huile', label: 'Filtre à huile' },
  { value: 'batterie', label: 'Batterie' },
  { value: 'autre', label: 'Autre' },
];

const ServiceReasonModal = ({ open, onClose, onSubmit, busy }) => {
  const [selected, setSelected] = useState('');
  const [custom, setCustom] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    setSelected('');
    setCustom('');
    setErr('');
  }, [open]);

  const handleSubmit = () => {
    const value = selected === 'autre' ? custom.trim() : selected;
    if (!value) return setErr('Choisissez ou saisissez le service effectué.');
    onSubmit(value, selected);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      subtitle="Dernière étape"
      title="Service effectué"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Annuler</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      {err && <ErrorNote>{err}</ErrorNote>}
      <Field label="Type de service" hint="Sert à programmer le prochain entretien du véhicule.">
        <select className={selectClass} value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Choisir un service…</option>
          {SERVICE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </Field>
      {selected === 'autre' && (
        <Field label="Préciser le service">
          <input
            className={inputClass}
            placeholder="Ex : remplacement pompe à eau"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
          />
        </Field>
      )}
    </Modal>
  );
};

const WorkshopLogMaintenance = () => {
  const { token, user } = useAuth();
  // Callers link here with ?vehicle=<id> (dashboard queue "Saisir", assigned
  // vehicles table). The param was being ignored, so the workshop had to find
  // the vehicle again in the dropdown it had just clicked.
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    vehicleId: searchParams.get('vehicle') || '',
    quotationId: '',
    technician: user?.name || '',
    notes: '',
    odometerReading: '',
    date: '',
  });
  const [removedParts, setRemovedParts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const { data: vehicles = EMPTY, error: errorVehicles } = useQuery({
    queryKey: ['assignedVehicles', token],
    queryFn: () => fetchAssignedVehicles(token),
    enabled: !!token,
  });

  const { data: quotations = EMPTY, error: errorQuotations } = useQuery({
    queryKey: ['confirmedQuotations', form.vehicleId],
    queryFn: () => (form.vehicleId ? fetchConfirmedQuotations(form.vehicleId, token) : EMPTY),
    enabled: !!form.vehicleId,
  });

  // What this car has already had done. The form used to ask for a reading and
  // a service with no idea what came before, so nothing caught a plate typed
  // into the wrong row or an odometer entered below the last one on record.
  const { data: history = EMPTY } = useQuery({
    queryKey: ['vehicleHistory', form.vehicleId],
    queryFn: () => fetchVehicleHistory(form.vehicleId, token),
    enabled: !!form.vehicleId && !!token,
  });

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v._id === form.vehicleId) || null,
    [vehicles, form.vehicleId]
  );

  // The highest reading anyone has recorded — the vehicle record or any past
  // log. An odometer cannot go backwards, so this is the floor for today.
  const lastKm = useMemo(() => {
    const readings = [
      Number(selectedVehicle?.odometerReading) || 0,
      ...history.map((h) => Number(h.odometerReading) || 0),
    ];
    return Math.max(0, ...readings);
  }, [selectedVehicle, history]);

  const enteredKm = Number(form.odometerReading);
  const kmGoesBackwards =
    form.odometerReading !== '' && Number.isFinite(enteredKm) && lastKm > 0 && enteredKm < lastKm;

  // Derived, not state: the old version kept this in useState and refilled it
  // from an effect keyed on `quotations`, which looped forever once the query
  // default array changed identity every render.
  const quotedItems = useMemo(() => {
    if (!form.quotationId) return EMPTY;
    const q = quotations.find((x) => x._id === form.quotationId);
    return q ? (q.parts || q.items || EMPTY) : EMPTY;
  }, [form.quotationId, quotations]);

  // Picking a different devis clears which lines were not fitted.
  useEffect(() => { setRemovedParts([]); }, [form.quotationId]);

  const handlePartToggle = (idx) => {
    const part = quotedItems[idx];
    if (removedParts.find((rp) => rp.description === part.description)) {
      setRemovedParts(removedParts.filter((rp) => rp.description !== part.description));
    } else {
      setRemovedParts([...removedParts, { description: part.description, reason: '' }]);
    }
  };

  const handleRemoveReason = (idx, value) => {
    setRemovedParts((rps) => rps.map((rp, i) => (i === idx ? { ...rp, reason: value } : rp)));
  };

  const {
    mutate: submitMaintenance, isPending, isSuccess, isError, error,
  } = useMutation({
    mutationFn: async (payload) => {
      const data = await logFromQuotation(payload, token);
      setModalOpen(false);
      setForm({
        vehicleId: '',
        quotationId: '',
        technician: user?.name || '',
        notes: '',
        odometerReading: '',
        date: '',
      });
      setRemovedParts([]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return data;
    },
    // Close the modal so the error banner on the page is visible.
    onError: () => setModalOpen(false),
  });

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // On main form submit: only open the modal, do NOT call the API yet.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.quotationId) return;
    setModalOpen(true);
  };

  const handleServiceReasonSubmit = (reason, type) => {
    const odometer = form.odometerReading !== '' ? Number(form.odometerReading) : undefined;
    submitMaintenance({
      vehicleId: form.vehicleId,
      workshopId: user._id,
      quotationId: form.quotationId,
      removedParts,
      technician: form.technician,
      notes: form.notes,
      odometerReading: odometer,
      date: form.date,
      serviceReason: reason,
      serviceType: type || reason,
    });
  };

  const errMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message;

  return (
    <Page title="Saisir un entretien" subtitle="Atelier" width="narrow">
      {isSuccess && !modalOpen && <Note tone="ok">Entretien enregistré et véhicule mis à jour.</Note>}
      {isError && <ErrorNote>{errMsg || "Échec de l'enregistrement."}</ErrorNote>}
      {errorVehicles && <ErrorNote>Chargement des véhicules : {errorVehicles.message}</ErrorNote>}
      {errorQuotations && <ErrorNote>Chargement des devis : {errorQuotations.message}</ErrorNote>}

      <form onSubmit={handleSubmit} className="flex flex-col gap-[18px]">
        <Panel title="Travaux réalisés">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <Field label="Véhicule" required>
              <SearchableSelect
                options={vehicles.map((v) => ({
                  value: v._id,
                  label: `${v.plateNumber} — ${v.brand || v.make || ''} ${v.model || ''}`.trim(),
                }))}
                value={form.vehicleId}
                onChange={(val) => handleChange({ target: { name: 'vehicleId', value: val } })}
                placeholder="Sélectionner un véhicule"
                name="vehicleId"
                required
              />
            </Field>
            {form.vehicleId && (
              <Field
                label="Devis confirmé"
                required
                hint={quotations.length === 0 ? 'Aucun devis confirmé pour ce véhicule.' : undefined}
              >
                <select name="quotationId" value={form.quotationId} onChange={handleChange}
                        required className={selectClass}>
                  <option value="">Sélectionner un devis</option>
                  {quotations.map((q) => (
                    <option key={q._id} value={q._id}>
                      {q.documentNumber || `Devis ${q._id.slice(-4)}`} — {fmt(q.clientTotal || q.total || 0)} F
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field
              label="Kilométrage (km)"
              required
              hint={lastKm > 0 ? `Dernier relevé connu : ${fmt(lastKm)} km` : undefined}
            >
              <input type="number" name="odometerReading" value={form.odometerReading}
                     onChange={handleChange} className={inputClass}
                     min={lastKm || undefined} required />
              {kmGoesBackwards && (
                <span className="block text-[10.5px] text-[#E2574B] mt-1">
                  Inférieur au dernier relevé ({fmt(lastKm)} km) — plaque ou saisie erronée ?
                </span>
              )}
            </Field>
            <Field label="Date" required>
              <input type="date" name="date" value={form.date} onChange={handleChange}
                     className={inputClass} required />
            </Field>
          </div>
        </Panel>

        {/* What this car has already had done. Sitting directly under the
            vehicle picker so a mis-picked plate is obvious before anything is
            typed: a Hilux showing a history of Porsche work is the wrong car. */}
        {form.vehicleId && (
          <Panel
            title="Historique du véhicule"
            action={
              <span className="text-[11px] text-[#6E665C]">
                {history.length === 0
                  ? 'aucun entretien enregistré'
                  : `${history.length} entretien${history.length > 1 ? 's' : ''}`}
              </span>
            }
          >
            {history.length === 0 ? (
              <p className="text-[12px] text-[#6E665C]">
                Premier entretien enregistré pour ce véhicule sur 1PARC.
              </p>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {history.slice(0, 3).map((h) => (
                  <li key={h._id} className="flex items-baseline justify-between gap-4">
                    <span className="min-w-0">
                      <span className="text-[12.5px] text-[#F4F1EA]">
                        {h.serviceReason || h.description || 'Entretien'}
                      </span>
                      {Array.isArray(h.partsUsed) && h.partsUsed.length > 0 && (
                        <span className="block text-[11px] text-[#6E665C] truncate">
                          {h.partsUsed.map((p) => p.description).filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block font-['Space_Grotesk',sans-serif] tabular-nums text-[12px] text-[#C9C3B6]">
                        {h.odometerReading ? `${fmt(h.odometerReading)} km` : '— km'}
                      </span>
                      <span className="block text-[10.5px] text-[#6E665C]">
                        {h.date || h.performedDate
                          ? new Date(h.date || h.performedDate).toLocaleDateString('fr-FR')
                          : ''}
                      </span>
                    </span>
                  </li>
                ))}
                {history.length > 3 && (
                  <li className="text-[10.5px] text-[#5C554C]">
                    + {history.length - 3} entretien{history.length - 3 > 1 ? 's' : ''} plus ancien
                    {history.length - 3 > 1 ? 's' : ''}
                  </li>
                )}
              </ul>
            )}
          </Panel>
        )}

        {quotedItems.length > 0 && (
          <Panel
            title="Lignes du devis"
            action={
              <span className="text-[11px] text-[#6E665C]">
                {quotedItems.length - removedParts.length}/{quotedItems.length} posées
              </span>
            }
          >
            <p className="text-[11.5px] text-[#8F877B] mb-3">
              Décochez ce qui n'a pas été posé, puis indiquez pourquoi.
            </p>
            <ul className="flex flex-col gap-2">
              {quotedItems.map((part, idx) => {
                const removedIdx = removedParts.findIndex((rp) => rp.description === part.description);
                const kept = removedIdx === -1;
                return (
                  <li
                    key={idx}
                    className={`rounded-xl border px-3.5 py-2.5 transition-colors ${
                      kept ? 'border-[#241F1B] bg-[#141210]' : 'border-[#3A2622] bg-[#1A1210]'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <Checkbox
                        checked={kept}
                        onChange={() => handlePartToggle(idx)}
                        label={
                          <span className={kept ? 'text-[#F4F1EA]' : 'text-[#8F877B] line-through'}>
                            {part.description}
                          </span>
                        }
                      />
                      <span className="ml-auto font-['Space_Grotesk',sans-serif] tabular-nums text-[12px] text-[#8F877B]">
                        {part.price ? `${fmt(part.price)} F` : ''}
                      </span>
                    </div>
                    {!kept && (
                      <input
                        type="text"
                        value={removedParts[removedIdx].reason}
                        onChange={(e) => handleRemoveReason(removedIdx, e.target.value)}
                        className={`${inputClass} mt-2.5 h-[32px] text-[12px]`}
                        placeholder="Motif (ex : pièce indisponible, refus du client)"
                        required
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </Panel>
        )}

        <Panel title="Détails">
          <div className="flex flex-col gap-3.5">
            <Field label="Technicien" required>
              <input type="text" name="technician" value={form.technician} onChange={handleChange}
                     className={inputClass} required />
            </Field>
            <Field label="Notes">
              <textarea name="notes" value={form.notes} onChange={handleChange}
                        rows={3} className={textareaClass}
                        placeholder="Observations, pièces d'usure à surveiller…" />
            </Field>
          </div>
        </Panel>

        <div>
          <Button
            as="button" type="submit" variant="primary"
            disabled={isPending || !form.quotationId}
            className="w-full sm:w-auto"
          >
            {isPending ? 'Enregistrement…' : "Enregistrer l'entretien"}
          </Button>
        </div>
      </form>

      <ServiceReasonModal
        open={modalOpen}
        busy={isPending}
        onClose={() => setModalOpen(false)}
        onSubmit={handleServiceReasonSubmit}
      />
    </Page>
  );
};

export default WorkshopLogMaintenance;
